import { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from '@/components/ui/button';
import { RichText } from '@/components/ui/rich-text';
import { Typography } from '@/components/ui/typography';
import { useJobPostingContext } from '@/contexts/job-posting-context';

interface MessagePreviewProps {
  openingId: string;
  onViewMore: () => void;
}

export function MessagePreview({ openingId, onViewMore }: MessagePreviewProps) {
  const { hookForm } = useJobPostingContext();
  const { watch } = hookForm;
  const subject = watch(`pool.openingMessages.${openingId}.subject`);
  const body = watch(`pool.openingMessages.${openingId}.body`);
  const contentRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  /* Check if the content is truncated */
  useEffect(() => {
    if (contentRef.current) {
      setIsTruncated(
        contentRef.current.scrollHeight > contentRef.current.clientHeight,
      );
    }
  }, [body]);

  return (
    <div className="border-fx-neutral-200 flex flex-col rounded-md border">
      {/* Subject */}
      <div className="flex items-center gap-5 px-2 py-2.5">
        <Typography variant="subtitle1">
          <FormattedMessage
            defaultMessage="Subject"
            description="Subject of the message"
          />
        </Typography>
        <Typography variant="body1">{subject}</Typography>
      </div>

      <div className="bg-fx-neutral-200 h-px w-full" />

      {/* Body */}
      <div className="gap-2.5 px-2 py-5">
        <RichText ref={contentRef} html={body} className="line-clamp-8" />

        {isTruncated && (
          <Button type="button" variant="link" onClick={onViewMore}>
            <FormattedMessage
              defaultMessage="View more"
              description="View more message"
            />
          </Button>
        )}
      </div>
    </div>
  );
}
